// pattern: Imperative Shell
// Flashcard editor — teacher interface for reviewing and editing flashcard decks

import { createElement, addListener, clearElement } from '../shell/dom.ts';
import { updateQuiz } from '../shell/api-client.ts';
import { navigate, routeToHash } from '../shell/router.ts';
import type { StoredQuiz } from '../core/question-model.ts';
import type { StoredFlashcard } from '../core/flashcard-model.ts';
import { generateFlashcardId } from '../core/flashcard-model.ts';

type EditableFlashcard = {
  id: string;
  front: string;
  back: string;
  topic: string;
  source: 'extracted' | 'generated' | 'manual';
  figureDataUrl?: string;
};

type FlashcardEditorState = {
  deckName: string;
  cards: Array<EditableFlashcard>;
  dirty: boolean;
  editingCardId: string | null;
};

function toEditable(fc: StoredFlashcard): EditableFlashcard {
  return {
    id: fc.id,
    front: fc.front,
    back: fc.back,
    topic: fc.topic ?? '',
    source: fc.source,
    ...(fc.figureDataUrl ? { figureDataUrl: fc.figureDataUrl } : {}),
  };
}

/**
 * Render flashcard editor. Accepts pre-fetched quiz to avoid double-fetching.
 * Called from editor-view.ts when quiz.mode === 'flashcard'.
 */
export function renderFlashcardEditor(
  container: HTMLElement,
  dashboardId: string,
  quizId: string,
  quiz: StoredQuiz
): () => void {
  const cleanups: Array<() => void> = [];

  const state: FlashcardEditorState = {
    deckName: quiz.name,
    cards: (quiz.flashcards ?? []).map(toEditable),
    dirty: false,
    editingCardId: null,
  };

  // Back button
  const backBtn = createElement('button', {
    className: 'btn btn-secondary editor-back-btn',
    textContent: '\u2190 Back to Dashboard',
  });
  const backCleanup = addListener(backBtn, 'click', () => {
    if (state.dirty && !confirm('You have unsaved changes. Leave anyway?')) return;
    navigate(routeToHash({ type: 'dashboard', dashboardId }));
  });
  cleanups.push(backCleanup);
  container.appendChild(backBtn);

  // Deck name
  const nameLabel = createElement('label', { textContent: 'Deck Name', className: 'editor-label' });
  const nameInput = createElement('input', {
    className: 'input editor-input',
    type: 'text',
    value: state.deckName,
  });
  const nameCleanup = addListener(nameInput, 'input', () => {
    state.deckName = nameInput.value;
    state.dirty = true;
  });
  cleanups.push(nameCleanup);
  container.append(nameLabel, nameInput);

  // Cards header
  const cardsHeader = createElement('h2', { className: 'flashcard-editor-header' });
  container.appendChild(cardsHeader);

  // Cards container
  const cardsContainer = createElement('div', { className: 'editor-cards' });
  container.appendChild(cardsContainer);

  // Add card button
  const addBtn = createElement('button', {
    className: 'btn btn-secondary flashcard-editor-add-btn',
    textContent: '+ Add Card',
  });
  const addCleanup = addListener(addBtn, 'click', () => {
    state.cards.push({
      id: generateFlashcardId(),
      front: '',
      back: '',
      topic: '',
      source: 'manual',
    });
    state.dirty = true;
    state.editingCardId = state.cards[state.cards.length - 1]!.id;
    renderCards();
  });
  cleanups.push(addCleanup);
  container.appendChild(addBtn);

  // Share section
  const shareSection = createElement('div', { className: 'editor-share-section' });

  const shareLabel = createElement('h3', { textContent: 'Share', className: 'editor-share-label' });

  const quizUrl = `${window.location.origin}${window.location.pathname}?q=${quizId}`;
  const shareLinkEl = createElement('p', { textContent: quizUrl, className: 'editor-share-link' });

  const copyBtn = createElement('button', {
    className: 'btn btn-secondary',
    textContent: 'Copy Link',
  });
  const copyCleanup = addListener(copyBtn, 'click', () => {
    void navigator.clipboard.writeText(quizUrl);
    copyBtn.textContent = 'Copied!';
    setTimeout(() => {
      copyBtn.textContent = 'Copy Link';
    }, 2000);
  });
  cleanups.push(copyCleanup);

  shareSection.append(shareLabel, shareLinkEl, copyBtn);
  container.appendChild(shareSection);

  // Action buttons
  const actionsDiv = createElement('div', { className: 'editor-actions' });

  const saveBtn = createElement('button', {
    className: 'btn btn-primary editor-save-btn',
    textContent: 'Save Changes',
  });

  const saveCleanup = addListener(saveBtn, 'click', () => {
    void handleSave(saveBtn);
  });
  cleanups.push(saveCleanup);

  actionsDiv.append(saveBtn);
  container.appendChild(actionsDiv);

  // Card-specific cleanups
  let cardCleanups: Array<() => void> = [];
  cleanups.push(() => cardCleanups.forEach((fn) => fn()));

  renderCards();

  function renderCards(): void {
    cardCleanups.forEach((fn) => fn());
    cardCleanups = [];

    cardsHeader.textContent = `Cards (${state.cards.length})`;
    clearElement(cardsContainer);

    if (state.cards.length === 0) {
      const emptyMsg = createElement('p', {
        textContent: 'No cards yet. Add one to get started!',
        className: 'empty-state',
      });
      cardsContainer.appendChild(emptyMsg);
      return;
    }

    state.cards.forEach((card, index) => {
      const isEditing = state.editingCardId === card.id;
      const el = isEditing ? renderEditCard(card, index) : renderDisplayCard(card, index);
      cardsContainer.appendChild(el);
    });
  }

  function renderDisplayCard(card: EditableFlashcard, index: number): HTMLElement {
    const el = createElement('div', { className: 'card flashcard-display-card' });
    el.setAttribute('data-card-display', '');

    if (card.figureDataUrl) {
      const figureDiv = createElement('div', { className: 'question-figure' });
      const figureImg = createElement('img') as HTMLImageElement;
      figureImg.src = card.figureDataUrl;
      figureImg.alt = 'Card figure';
      figureDiv.appendChild(figureImg);
      el.appendChild(figureDiv);
    }

    const frontLabel = createElement('p', { className: 'text-secondary flashcard-card-label' });
    frontLabel.textContent = 'Front:';

    const frontText = createElement('p', { className: 'flashcard-card-front-text' });
    frontText.textContent = `${index + 1}. ${card.front || '(empty)'}`;

    const backLabel = createElement('p', { className: 'text-secondary flashcard-card-label' });
    backLabel.textContent = 'Back:';

    const backText = createElement('p', { className: 'flashcard-card-back-text' });
    backText.textContent = card.back || '(empty)';

    const meta = createElement('p', { className: 'text-secondary flashcard-card-meta' });
    meta.textContent = `Topic: ${card.topic || 'none'}  Source: ${card.source}`;

    const actions = createElement('div', { className: 'flashcard-card-actions' });

    const editBtn = createElement('button', {
      className: 'btn btn-secondary question-edit-btn',
      textContent: 'Edit',
    });
    const editCleanup = addListener(editBtn, 'click', () => {
      state.editingCardId = card.id;
      renderCards();
    });
    cardCleanups.push(editCleanup);

    const delBtn = createElement('button', {
      className: 'btn btn-danger question-delete-btn',
      textContent: 'Delete',
    });
    const delCleanup = addListener(delBtn, 'click', () => {
      if (!confirm(`Delete card ${index + 1}?`)) return;
      state.cards.splice(index, 1);
      state.dirty = true;
      renderCards();
    });
    cardCleanups.push(delCleanup);

    actions.append(editBtn, delBtn);
    el.append(frontLabel, frontText, backLabel, backText, meta, actions);
    return el;
  }

  function renderEditCard(card: EditableFlashcard, index: number): HTMLElement {
    const el = createElement('div', { className: 'card flashcard-edit-card editing' });
    el.setAttribute('data-card-edit', '');

    const heading = createElement('h3', {
      textContent: `Card ${index + 1}`,
      className: 'flashcard-edit-heading',
    });
    el.appendChild(heading);

    if (card.figureDataUrl) {
      const figureDiv = createElement('div', { className: 'question-figure' });
      const figureLabel = createElement('p', {
        textContent: 'Figure (from PDF)',
        className: 'text-secondary question-figure-label',
      });
      const figureImg = createElement('img') as HTMLImageElement;
      figureImg.src = card.figureDataUrl;
      figureImg.alt = 'Card figure';
      figureDiv.append(figureLabel, figureImg);
      el.appendChild(figureDiv);
    }

    // Front
    const frontLabel = createElement('label', {
      textContent: 'Front',
      className: 'editor-field-label',
    });
    const frontInput = createElement('textarea', {
      className: 'input editor-field-input',
      value: card.front,
    });
    frontInput.style.minHeight = '60px';
    frontInput.style.resize = 'vertical';
    const frontCleanup = addListener(frontInput, 'input', () => {
      card.front = frontInput.value;
      state.dirty = true;
    });
    cardCleanups.push(frontCleanup);

    // Back
    const backLabel = createElement('label', {
      textContent: 'Back',
      className: 'editor-field-label',
    });
    const backInput = createElement('textarea', {
      className: 'input editor-field-input',
      value: card.back,
    });
    backInput.style.minHeight = '60px';
    backInput.style.resize = 'vertical';
    const backCleanup = addListener(backInput, 'input', () => {
      card.back = backInput.value;
      state.dirty = true;
    });
    cardCleanups.push(backCleanup);

    // Topic
    const topicLabel = createElement('label', {
      textContent: 'Topic',
      className: 'editor-field-label',
    });
    const topicInput = createElement('input', {
      className: 'input editor-field-input',
      type: 'text',
      value: card.topic,
    });
    const topicCleanup = addListener(topicInput, 'input', () => {
      card.topic = topicInput.value;
      state.dirty = true;
    });
    cardCleanups.push(topicCleanup);

    // Done button
    const doneBtn = createElement('button', {
      className: 'btn btn-primary',
      textContent: 'Done Editing',
    });
    const doneCleanup = addListener(doneBtn, 'click', () => {
      state.editingCardId = null;
      renderCards();
    });
    cardCleanups.push(doneCleanup);

    el.append(frontLabel, frontInput, backLabel, backInput, topicLabel, topicInput, doneBtn);
    return el;
  }

  async function handleSave(saveBtn: HTMLButtonElement): Promise<void> {
    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving...';

    const result = await updateQuiz(quizId, {
      dashboardId,
      name: state.deckName,
      mode: 'flashcard' as const,
      flashcards: state.cards.map((c) => ({
        id: c.id,
        front: c.front,
        back: c.back,
        topic: c.topic || undefined,
        source: c.source,
        ...(c.figureDataUrl ? { figureDataUrl: c.figureDataUrl } : {}),
      })),
      questions: [],
    });

    if (result.ok) {
      state.dirty = false;
      saveBtn.textContent = 'Saved!';
      setTimeout(() => {
        saveBtn.textContent = 'Save Changes';
        saveBtn.disabled = false;
      }, 2000);
    } else {
      saveBtn.textContent = 'Save Failed';
      saveBtn.disabled = false;
      setTimeout(() => {
        saveBtn.textContent = 'Save Changes';
      }, 2000);
    }
  }

  return () => cleanups.forEach((fn) => fn());
}
